/**
 * React Query hooks for /api/v1/pipeline-facility/* (fieldiq-api#1196, PF-1).
 *
 * EVERY HOOK RETURNS THE META. A screen cannot label a chainage, a diameter or
 * a pressure without `meta.units`, and the values arrive in those units.
 *
 * EVERY KEY STARTS ['pipelineFacility'] and every write invalidates that one
 * prefix: a segment write moves the site's segment count on the register, a
 * site write moves the register and the detail.
 *
 * EVERY READ SPREADS LOAD_FAILURE_QUERY_OPTIONS, so a refusal is not retried
 * and reads as a refusal (queryLoadFailure), never as an empty list.
 */
import { useMutation, useQueries, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { unwrapArray } from '../lib/api-envelope';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../lib/load-failure';
import type {
  PfCatalog,
  PfCustomerSettings,
  PfCustomerSettingsUpdate,
  PfMeta,
  PfResult,
  PfSegment,
  PfSegmentWrite,
  PfSettings,
  PfSettingsUpdate,
  PfSite,
  PfSiteDetail,
  PfSiteWrite,
  PfJob,
  PfJobDetail,
  PfJobWrite,
  PfCrewWrite,
  PfJobEquipmentWrite,
  PfRentInWrite,
  PfDailyLog,
  PfDailyLogSummary,
  PfDailyLogWrite,
  PfLogWarning,
  PfPreUseCheck,
  PfFieldJob,
  PfFieldJobDetail,
  PfFieldLog,
  PfFieldLogWarning,
  PfFieldMeta,
} from '../types/pipeline-facility';

/** No zone at all — absent meta prints no time, never a guessed zone. */
const NO_ZONE: string | null = null;

const EMPTY_META: PfMeta = {
  unitSystem: null,
  unitSystemSource: null,
  units: null,
  currency: null,
  country: null,
  timeZone: NO_ZONE,
  today: null,
};

/**
 * Split a body into payload and meta. `data: null` stays null — never the
 * envelope (the `res.data?.data || res.data` bug). A missing meta is all-null,
 * so a screen prints no unit rather than guessing one.
 */
export function unwrapPf<T>(body: unknown): PfResult<T | null> {
  if (body === null || typeof body !== 'object' || Array.isArray(body)) return { data: null, meta: EMPTY_META };
  const b = body as { data?: unknown; meta?: Partial<PfMeta> };
  const data = 'data' in b && b.data !== undefined && b.data !== null ? (b.data as T) : null;
  return { data, meta: { ...EMPTY_META, ...(b.meta ?? {}) } };
}

/** A list read: a non-array payload becomes `[]` rather than reaching `.map`. */
export function unwrapPfList<T>(body: unknown): PfResult<T[]> {
  const { data, meta } = unwrapPf<unknown>(body);
  return { data: Array.isArray(data) ? (data as T[]) : [], meta };
}

export interface PfSiteFilters {
  kind?: string;
  customerId?: string;
  status?: string;
  q?: string;
}

export interface PfJobFilters {
  status?: string;
  siteId?: string;
  customerId?: string;
  q?: string;
}

const clean = (p?: PfSiteFilters | PfJobFilters) => {
  if (!p) return undefined;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(p)) if (v) out[k] = v;
  return Object.keys(out).length ? out : undefined;
};

export const pfKeys = {
  all: ['pipelineFacility'] as const,
  meta: () => ['pipelineFacility', 'meta'] as const,
  settings: () => ['pipelineFacility', 'settings'] as const,
  customerSettings: (id: string) => ['pipelineFacility', 'customerSettings', id] as const,
  customers: () => ['pipelineFacility', 'customers'] as const,
  sites: (p?: PfSiteFilters) => ['pipelineFacility', 'sites', clean(p) ?? {}] as const,
  site: (id: string) => ['pipelineFacility', 'site', id] as const,
  jobs: (p?: PfJobFilters) => ['pipelineFacility', 'jobs', clean(p) ?? {}] as const,
  job: (id: string) => ['pipelineFacility', 'job', id] as const,
  logs: (jobId: string) => ['pipelineFacility', 'logs', jobId] as const,
  log: (id: string) => ['pipelineFacility', 'log', id] as const,
  preUse: (jobId: string, date: string) => ['pipelineFacility', 'preUse', jobId, date] as const,
  users: () => ['pipelineFacility', 'users'] as const,
  equipment: () => ['pipelineFacility', 'equipment'] as const,
  fieldJobs: () => ['pipelineFacility', 'field', 'jobs'] as const,
  fieldJob: (id: string) => ['pipelineFacility', 'field', 'job', id] as const,
  fieldLog: (jobId: string, day: string) => ['pipelineFacility', 'field', 'log', jobId, day] as const,
};

export const invalidatePf = (qc: QueryClient) => qc.invalidateQueries({ queryKey: pfKeys.all });

/** The catalog (codes, kinds, products, statuses, ticket modes) with the API's labels. */
export function usePfMeta() {
  return useQuery({
    queryKey: pfKeys.meta(),
    queryFn: async () => unwrapPf<PfCatalog>((await api.pipelineFacility.meta()).data),
    staleTime: 5 * 60_000,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function usePfSettings() {
  return useQuery({
    queryKey: pfKeys.settings(),
    queryFn: async () => unwrapPf<PfSettings>((await api.pipelineFacility.settings.get()).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useUpdatePfSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: PfSettingsUpdate) => unwrapPf<PfSettings>((await api.pipelineFacility.settings.update(body)).data),
    onSuccess: () => invalidatePf(qc),
  });
}

export function usePfCustomerSettings(customerId: string | undefined) {
  return useQuery({
    queryKey: pfKeys.customerSettings(customerId ?? ''),
    queryFn: async () =>
      unwrapPf<PfCustomerSettings>((await api.pipelineFacility.customerSettings.get(customerId as string)).data),
    enabled: !!customerId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** One settings read per customer, for the settings page's customer list. */
export function usePfCustomerSettingsList(customerIds: string[]) {
  return useQueries({
    queries: customerIds.map((id) => ({
      queryKey: pfKeys.customerSettings(id),
      queryFn: async () => unwrapPf<PfCustomerSettings>((await api.pipelineFacility.customerSettings.get(id)).data),
      ...LOAD_FAILURE_QUERY_OPTIONS,
    })),
  });
}

export function useUpdatePfCustomerSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ customerId, body }: { customerId: string; body: PfCustomerSettingsUpdate }) =>
      unwrapPf<PfCustomerSettings>((await api.pipelineFacility.customerSettings.update(customerId, body)).data),
    onSuccess: () => invalidatePf(qc),
  });
}

/** The tenant's customers, for the site form's picker and the settings list. Office reads only. */
export function usePfCustomers(enabled = true) {
  return useQuery({
    queryKey: pfKeys.customers(),
    queryFn: async () =>
      unwrapArray<{ id: string; name?: string | null; companyName?: string | null }>((await api.customers.list()).data)
        .map((c) => ({ id: c.id, name: c.name || c.companyName || '—' }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function usePfSites(filters?: PfSiteFilters) {
  return useQuery({
    queryKey: pfKeys.sites(filters),
    queryFn: async () => unwrapPfList<PfSite>((await api.pipelineFacility.sites.list(clean(filters))).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function usePfSite(id: string | undefined) {
  return useQuery({
    queryKey: pfKeys.site(id ?? ''),
    queryFn: async () => unwrapPf<PfSiteDetail>((await api.pipelineFacility.sites.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useCreatePfSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: PfSiteWrite) => unwrapPf<PfSiteDetail>((await api.pipelineFacility.sites.create(body)).data),
    onSuccess: () => invalidatePf(qc),
  });
}

export function useUpdatePfSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: PfSiteWrite }) =>
      unwrapPf<PfSiteDetail>((await api.pipelineFacility.sites.update(id, body)).data),
    onSuccess: () => invalidatePf(qc),
  });
}

export function useDeletePfSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.pipelineFacility.sites.remove(id)).data,
    onSuccess: () => invalidatePf(qc),
  });
}

export function useCreatePfSegment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ siteId, body }: { siteId: string; body: PfSegmentWrite }) =>
      unwrapPf<PfSegment>((await api.pipelineFacility.segments.create(siteId, body)).data),
    onSuccess: () => invalidatePf(qc),
  });
}

export function useUpdatePfSegment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: PfSegmentWrite }) =>
      unwrapPf<PfSegment>((await api.pipelineFacility.segments.update(id, body)).data),
    onSuccess: () => invalidatePf(qc),
  });
}

export function useDeletePfSegment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.pipelineFacility.segments.remove(id)).data,
    onSuccess: () => invalidatePf(qc),
  });
}

// ═══ PF-2 (fieldiq-api#1200): jobs, crew, equipment, rent-ins, daily logs ═══

export function usePfJobs(filters?: PfJobFilters) {
  return useQuery({
    queryKey: pfKeys.jobs(filters),
    queryFn: async () => unwrapPfList<PfJob>((await api.pipelineFacility.jobs.list(clean(filters))).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function usePfJob(id: string | undefined) {
  return useQuery({
    queryKey: pfKeys.job(id ?? ''),
    queryFn: async () => unwrapPf<PfJobDetail>((await api.pipelineFacility.jobs.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** Every write here invalidates the whole prefix: a crew add moves the register count and the detail. */
function usePfWrite<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => invalidatePf(qc) });
}

export const useCreatePfJob = () =>
  usePfWrite(async (body: PfJobWrite) => unwrapPf<PfJobDetail>((await api.pipelineFacility.jobs.create(body)).data));
export const useUpdatePfJob = () =>
  usePfWrite(async ({ id, body }: { id: string; body: PfJobWrite }) =>
    unwrapPf<PfJobDetail>((await api.pipelineFacility.jobs.update(id, body)).data));
export const useDeletePfJob = () => usePfWrite(async (id: string) => (await api.pipelineFacility.jobs.remove(id)).data);

export const useAddPfCrew = () =>
  usePfWrite(async ({ jobId, body }: { jobId: string; body: PfCrewWrite }) => (await api.pipelineFacility.crew.add(jobId, body)).data);
export const useRemovePfCrew = () => usePfWrite(async (id: string) => (await api.pipelineFacility.crew.remove(id)).data);

export const useAddPfJobEquipment = () =>
  usePfWrite(async ({ jobId, body }: { jobId: string; body: PfJobEquipmentWrite }) =>
    (await api.pipelineFacility.jobEquipment.add(jobId, body)).data);
export const useRemovePfJobEquipment = () =>
  usePfWrite(async (id: string) => (await api.pipelineFacility.jobEquipment.remove(id)).data);

export const useAddPfRentIn = () =>
  usePfWrite(async ({ jobId, body }: { jobId: string; body: PfRentInWrite }) => (await api.pipelineFacility.rentIns.add(jobId, body)).data);
export const useUpdatePfRentIn = () =>
  usePfWrite(async ({ id, body }: { id: string; body: PfRentInWrite }) => (await api.pipelineFacility.rentIns.update(id, body)).data);
export const useRemovePfRentIn = () => usePfWrite(async (id: string) => (await api.pipelineFacility.rentIns.remove(id)).data);

export function usePfDailyLogs(jobId: string | undefined) {
  return useQuery({
    queryKey: pfKeys.logs(jobId ?? ''),
    queryFn: async () => unwrapPfList<PfDailyLogSummary>((await api.pipelineFacility.dailyLogs.list(jobId as string)).data),
    enabled: !!jobId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function usePfDailyLog(id: string | undefined) {
  return useQuery({
    queryKey: pfKeys.log(id ?? ''),
    queryFn: async () => unwrapPf<PfDailyLog>((await api.pipelineFacility.dailyLogs.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

const warningsOf = (body: unknown): PfLogWarning[] => {
  const w = (body as { warnings?: unknown } | null)?.warnings;
  return Array.isArray(w) ? (w as PfLogWarning[]) : [];
};

export const useCreatePfDailyLog = () =>
  usePfWrite(async ({ jobId, body }: { jobId: string; body: PfDailyLogWrite }) => {
    const raw = (await api.pipelineFacility.dailyLogs.create(jobId, body)).data;
    return { ...unwrapPf<PfDailyLog>(raw), warnings: warningsOf(raw) };
  });
export const useUpdatePfDailyLog = () =>
  usePfWrite(async ({ id, body }: { id: string; body: PfDailyLogWrite }) => {
    const raw = (await api.pipelineFacility.dailyLogs.update(id, body)).data;
    return { ...unwrapPf<PfDailyLog>(raw), warnings: warningsOf(raw) };
  });
export const useDeletePfDailyLog = () => usePfWrite(async (id: string) => (await api.pipelineFacility.dailyLogs.remove(id)).data);

/** Pre-use checks for the job's units on `date`. Off until there is a date to ask about. */
export function usePfPreUseChecks(jobId: string | undefined, date: string | undefined) {
  return useQuery({
    queryKey: pfKeys.preUse(jobId ?? '', date ?? ''),
    queryFn: async () => unwrapPfList<PfPreUseCheck>((await api.pipelineFacility.preUseChecks(jobId as string, date)).data),
    enabled: !!jobId && !!date,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** The tenant's users, for the crew and supervisor pickers (office only). */
export function usePfUsers(enabled = true) {
  return useQuery({
    queryKey: pfKeys.users(),
    queryFn: async () =>
      unwrapArray<{ id: string; firstName?: string; lastName?: string; fullName?: string; role?: string;
        first_name?: string; last_name?: string; employmentStatus?: string; employment_status?: string }>((await api.users.list()).data)
        .filter((u) => (u.employmentStatus ?? u.employment_status) !== 'terminated')
        .map((u) => ({
          id: u.id,
          name: (u.fullName || [u.firstName ?? u.first_name, u.lastName ?? u.last_name].filter(Boolean).join(' ')).trim() || '—',
          role: u.role ?? null,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** The tenant's equipment (owned units), for the job equipment and rent-in pickers. */
export function usePfEquipmentOptions(enabled = true) {
  return useQuery({
    queryKey: pfKeys.equipment(),
    queryFn: async () =>
      unwrapArray<{ id: string; name?: string | null; unitNumber?: string | null; unit_number?: string | null;
        serialNumber?: string | null; serial_number?: string | null; status?: string | null }>((await api.equipment.list()).data)
        .filter((e) => e.status !== 'retired')
        .map((e) => ({
          id: e.id,
          name: e.name || '—',
          unitNumber: e.unitNumber ?? e.unit_number ?? null,
          serialNumber: e.serialNumber ?? e.serial_number ?? null,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

// ─── Field surface (snake_case) ──────────────────────────────────────────────

export interface PfFieldResult<T> {
  data: T;
  meta: PfFieldMeta;
}

const EMPTY_FIELD_META: PfFieldMeta = { unit_system: null, units: null, currency: null, country: null, time_zone: null, today: null };

export function unwrapPfField<T>(body: unknown): PfFieldResult<T | null> {
  if (body === null || typeof body !== 'object' || Array.isArray(body)) return { data: null, meta: EMPTY_FIELD_META };
  const b = body as { data?: unknown; meta?: Partial<PfFieldMeta> };
  const data = b.data !== undefined && b.data !== null ? (b.data as T) : null;
  return { data, meta: { ...EMPTY_FIELD_META, ...(b.meta ?? {}) } };
}

export function usePfFieldJobs() {
  return useQuery({
    queryKey: pfKeys.fieldJobs(),
    queryFn: async () => {
      const r = unwrapPfField<PfFieldJob[]>((await api.pipelineFacility.field.myJobs()).data);
      return { data: Array.isArray(r.data) ? r.data : [], meta: r.meta };
    },
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function usePfFieldJob(id: string | undefined) {
  return useQuery({
    queryKey: pfKeys.fieldJob(id ?? ''),
    queryFn: async () => unwrapPfField<PfFieldJobDetail>((await api.pipelineFacility.field.myJob(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function usePfFieldLog(jobId: string | undefined, day: string | undefined) {
  return useQuery({
    queryKey: pfKeys.fieldLog(jobId ?? '', day ?? ''),
    queryFn: async () => unwrapPfField<PfFieldLog>((await api.pipelineFacility.field.log(jobId as string, day as string)).data),
    enabled: !!jobId && !!day,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useSavePfFieldLog = () =>
  usePfWrite(async ({ jobId, day, body }: { jobId: string; day: string; body: PfDailyLogWrite }) => {
    const raw = (await api.pipelineFacility.field.saveLog(jobId, day, body)).data;
    const w = (raw as { warnings?: unknown } | null)?.warnings;
    return { ...unwrapPfField<PfFieldLog>(raw), warnings: Array.isArray(w) ? (w as PfFieldLogWarning[]) : [] };
  });
